import { useEffect, useState } from "react";
import { X, ArrowRight, AlertTriangle } from "lucide-react";
import { translations } from "@/translations";

export const OPEN_EVENT = "cw:open-preorder";

const GOLD = "#c8a268";

// Launch event einde: 5 sep 2026, 10:00 CET (UTC+2 in september = CEST)
const LAUNCH_POPUP_END = new Date("2026-09-05T10:00:00+02:00");
// Voucher-variant einde: 30 sep 2026, 23:59 CEST (einde van de dag)
const DISCOUNT_POPUP_END = new Date("2026-09-30T23:59:59+02:00");

const isLaunchWindow = () => new Date() < LAUNCH_POPUP_END;
const isDiscountWindow = () => new Date() < DISCOUNT_POPUP_END;

/**
 * MarketingPopup — toont tot 5 sept 2026 10:00 CET de LaunchPopup
 * (wedstrijd/lancering in Tienen), daarna automatisch de standaard
 * "Not a t-shirt" popup. Beide vertaalbaar NL/EN.
 */
export default function PreorderPopup({ lang, onReserve }) {
  const [open, setOpen] = useState(true);

  useEffect(() => {
    setOpen(true);
  }, [lang]);

  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, h);
    return () => window.removeEventListener(OPEN_EVENT, h);
  }, []);

  if (!open) return null;

  const handleClose = () => setOpen(false);

  if (isLaunchWindow()) {
    return <LaunchPopup lang={lang} onClose={handleClose} />;
  }
  return <IdentityPopup lang={lang} onClose={handleClose} onReserve={onReserve} />;
}

/* Standaard popup — "Geen shirt / Een levende identiteit"
   Tot 30-sep-2026 EOD toont een voucher-variant met andere CTA. */
function IdentityPopup({ lang, onClose, onReserve }) {
  const t = translations[lang].preorder;
  const showDiscount = isDiscountWindow();
  const handleReserve = () => {
    onClose();
    if (onReserve) onReserve();
  };
  return (
    <div
      data-testid="marketing-popup"
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 md:p-8"
      role="dialog"
      aria-modal="true"
      aria-label="Connected Words · 1st Edition"
    >
      <button
        data-testid="marketing-popup-overlay"
        onClick={onClose}
        aria-label="Close overlay"
        className="absolute inset-0 bg-black/85 backdrop-blur-sm"
      />
      <div
        className="relative w-full max-w-[900px] max-h-[95vh] overflow-auto border"
        style={{
          borderColor: "rgba(200,162,104,0.45)",
          background:
            "radial-gradient(ellipse at 30% 0%, rgba(200,162,104,0.14), transparent 55%), linear-gradient(180deg, #101013 0%, #06060a 100%)",
        }}
      >
        <button
          data-testid="marketing-popup-close"
          onClick={onClose}
          aria-label={t.close}
          className="absolute top-3 right-3 z-10 w-10 h-10 flex items-center justify-center bg-black/60 border border-white/20 text-white/90 hover:bg-white hover:text-black transition-colors rounded-full"
        >
          <X size={18} />
        </button>
        <div className="px-8 md:px-14 py-12 md:py-16 text-center">
          <div className="text-[10px] tracking-[0.36em] uppercase" style={{ color: GOLD }}>
            {t.eyebrow}
          </div>
          <h2
            data-testid="marketing-popup-title"
            className="mt-6 font-serif text-white text-[38px] md:text-[64px] leading-[1.02] tracking-[-0.02em]"
          >
            {t.title}
            <br />
            <span style={{ color: GOLD, fontStyle: "italic" }}>{t.titleAccent}</span>
          </h2>
          <div className="mt-8 space-y-2 max-w-xl mx-auto">
            {t.lines.map((line, i) => (
              <p key={i} className="text-white/80 text-[14px] md:text-[16px] leading-[1.7]">
                {line}
              </p>
            ))}
          </div>

          {showDiscount && (
            <div
              data-testid="marketing-popup-discount-intro"
              className="mt-7 space-y-2 max-w-xl mx-auto"
            >
              {t.discountLines.map((line, i) => (
                <p
                  key={i}
                  className="text-white text-[16px] md:text-[19px] leading-[1.55]"
                  style={{ color: i === 0 ? GOLD : "rgba(255,255,255,0.92)" }}
                >
                  {line}
                </p>
              ))}
            </div>
          )}

          <div className="mt-10 flex items-center justify-center gap-6 text-[10px] tracking-[0.32em] uppercase text-white/45">
            <span>{t.metaLeft}</span>
            <span className="w-8 h-px bg-white/20" />
            <span>{t.metaRight}</span>
          </div>

          {showDiscount ? (
            <a
              data-testid="marketing-popup-discount-cta"
              href={t.discountUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-10 group inline-flex items-center gap-3 px-8 py-4 text-[11px] tracking-[0.22em] uppercase text-black transition-all hover:gap-4"
              style={{ backgroundColor: GOLD }}
            >
              {t.discountCta}
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
            </a>
          ) : (
            <button
              data-testid="marketing-popup-cta"
              onClick={handleReserve}
              className="mt-10 group inline-flex items-center gap-3 px-8 py-4 text-[11px] tracking-[0.22em] uppercase text-black transition-all hover:gap-4"
              style={{ backgroundColor: GOLD }}
            >
              {t.cta}
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
            </button>
          )}

          <p className="mt-8 text-white/45 text-[11.5px] leading-[1.6] max-w-md mx-auto">
            {t.note}
          </p>
        </div>
      </div>
    </div>
  );
}

/* Launch popup — actief tot 5 sept 2026 10:00 CET */
function LaunchPopup({ lang, onClose }) {
  const t = translations[lang].launchEvent;
  return (
    <div
      data-testid="launch-popup"
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 md:p-8"
      role="dialog"
      aria-modal="true"
      aria-label="Connected Words · Launch event"
    >
      <button
        data-testid="launch-popup-overlay"
        onClick={onClose}
        aria-label="Close overlay"
        className="absolute inset-0 bg-black/85 backdrop-blur-sm"
      />
      <div
        className="relative w-full max-w-[820px] max-h-[92vh] overflow-auto border"
        style={{
          borderColor: "rgba(200,162,104,0.45)",
          background:
            "radial-gradient(ellipse at 30% 0%, rgba(200,162,104,0.14), transparent 55%), linear-gradient(180deg, #101013 0%, #06060a 100%)",
        }}
      >
        <button
          data-testid="launch-popup-close"
          onClick={onClose}
          aria-label={t.close}
          className="sticky top-3 float-right z-10 w-10 h-10 flex items-center justify-center bg-black/70 border border-white/20 text-white/90 hover:bg-white hover:text-black transition-colors rounded-full mr-3"
        >
          <X size={18} />
        </button>

        <div className="px-6 md:px-12 py-10 md:py-14">
          {/* Eyebrow */}
          <div
            className="text-[10px] tracking-[0.36em] uppercase text-center"
            style={{ color: GOLD }}
          >
            {t.eyebrow}
          </div>

          {/* Title */}
          <h2
            data-testid="launch-popup-title"
            className="mt-5 font-serif text-white text-[32px] md:text-[52px] leading-[1.02] tracking-[-0.02em] text-center"
          >
            {t.title}{" "}
            <span style={{ color: GOLD, fontStyle: "italic" }}>{t.titleAccent}</span>
          </h2>

          {/* When */}
          <div
            className="mt-4 text-center text-[12px] md:text-[13px] tracking-[0.16em] uppercase"
            style={{ color: GOLD }}
          >
            {t.when}
          </div>

          {/* Intro */}
          <p className="mt-6 text-white/80 text-[14px] md:text-[15px] leading-[1.7] text-center max-w-2xl mx-auto">
            {t.intro}
          </p>

          {/* Sentence + Journey */}
          <div className="mt-8 text-center space-y-3">
            <div className="font-serif italic text-[16px] md:text-[20px]" style={{ color: GOLD }}>
              {t.sentence}
            </div>
            <div className="text-[11px] md:text-[13px] tracking-[0.32em] uppercase text-white/80">
              {t.journey}
            </div>
          </div>

          {/* Mechanic + warning */}
          <div className="mt-8 space-y-4 max-w-2xl mx-auto">
            <p className="text-white/85 text-[14px] leading-[1.7] text-center">
              {t.mechanic}
            </p>
            <div
              className="border p-4 flex gap-3 items-start"
              style={{
                borderColor: "rgba(200,162,104,0.35)",
                background:
                  "linear-gradient(180deg, rgba(200,162,104,0.08), rgba(200,162,104,0.02))",
              }}
            >
              <AlertTriangle size={16} className="shrink-0 mt-0.5" style={{ color: GOLD }} />
              <div>
                <div className="text-[10px] tracking-[0.28em] uppercase" style={{ color: GOLD }}>
                  {t.warningLabel}
                </div>
                <p className="mt-1.5 text-white/80 text-[13px] leading-[1.6]">
                  {t.warning}
                </p>
              </div>
            </div>
          </div>

          {/* How it works */}
          <div className="mt-10">
            <div className="text-[10px] tracking-[0.32em] uppercase text-center" style={{ color: GOLD }}>
              {t.howLabel}
            </div>
            <ol className="mt-5 space-y-2.5 max-w-2xl mx-auto">
              {t.how.map((step, i) => (
                <li key={i} className="grid grid-cols-[auto_1fr] gap-4 items-start">
                  <span className="font-serif text-[14px]" style={{ color: GOLD }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-white/80 text-[13.5px] leading-[1.6]">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Get ready */}
          <div className="mt-10">
            <div className="text-[10px] tracking-[0.32em] uppercase text-center" style={{ color: GOLD }}>
              {t.prepareLabel}
            </div>
            <ul className="mt-5 space-y-2.5 max-w-2xl mx-auto">
              {t.prepare.map((step, i) => (
                <li key={i} className="flex items-start gap-3 text-white/80 text-[13.5px] leading-[1.6]">
                  <span className="mt-2 inline-block w-1 h-1 shrink-0" style={{ backgroundColor: GOLD }} />
                  {step}
                </li>
              ))}
            </ul>

            {/* Action buttons */}
            <div className="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4 max-w-2xl mx-auto">
              <a
                data-testid="launch-popup-cw-btn"
                href="https://use1id-prd.vercel.app/r/workspaces/CONNECTED_WORDS/games/LAUNCH_1ST_EDITION"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-3 px-6 py-3.5 text-[11px] tracking-[0.22em] uppercase border text-white/90 hover:bg-white hover:text-black transition-all"
                style={{ borderColor: "rgba(200,162,104,0.6)" }}
              >
                {t.launchBtn}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
              </a>
              <a
                data-testid="launch-popup-register-btn"
                href={t.registerUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-3 px-6 py-3.5 text-[11px] tracking-[0.22em] uppercase text-black transition-all hover:gap-4"
                style={{ backgroundColor: GOLD }}
              >
                {t.registerBtn}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
              </a>
            </div>
          </div>

          {/* What makes this shirt unique */}
          <div className="mt-12 border-t border-white/10 pt-8">
            <div className="text-[10px] tracking-[0.32em] uppercase text-center" style={{ color: GOLD }}>
              {t.uniqueLabel}
            </div>
            <p className="mt-5 text-white/80 text-[14px] leading-[1.75] max-w-2xl mx-auto text-center">
              {t.unique}
            </p>
          </div>

          {/* Tagline */}
          <div className="mt-10 text-center">
            <div className="font-serif italic text-[18px] md:text-[22px] leading-[1.3]" style={{ color: GOLD }}>
              {t.taglineLine1}
            </div>
            <div className="mt-2 text-[11px] md:text-[13px] tracking-[0.28em] uppercase text-white/70">
              {t.taglineLine2}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
